import _get from 'lodash/get';
import _find from 'lodash/find';
import _findKey from 'lodash/findKey';
import _each from 'lodash/each';
import _isEmpty from 'lodash/isEmpty';
import { getDonateButtonImageUrl } from '../utils/buttonImage';
import {
	getSupportedVariables,
	getUnsupportedVariables,
	formatVariablesForTextArea,
	parseTextAreaValue
} from './extraVariables';
import getStore from '../store/configureStore';
import _includes from 'lodash/includes';
import { isLoggedInUser } from '../utils/authUtils';
import { formatAmountFromServer, normalizeNumber } from '../utils/amountUtils';

export const ACCOUNT_IDENTIFIER_TYPES = {
	MERCHANT_ACCOUNT_ID: 'merchantAccountId',
	EMAIL_ADDRESS: 'emailAddress'
}

// All supported server button props
export const SERVER_BUTTON = {
	ID: 'id',
	WEBSITE_CODE: 'website_code',
	EMAIL_CODE: 'email_code',
	CODE: 'code',
	TYPE: 'type',
	SUBTYPE: 'sub_type',
	BUTTON_IMAGE: 'button_image',
	BUTTON_IMAGE_URL: 'button_image_url',
	COUNTRY_CODE: 'country_code',
	LANGUAGE: 'language',
	BUTTON_VARIABLES: 'button_variables',
	CURRENCY_CODE: 'currency_code',
	LC: 'lc',
	BN: 'bn',
	ITEM_NAME: 'item_name',
	ITEM_NUMBER: 'item_number',
	NO_NOTE: 'no_note',
	NO_SHIPPING: 'no_shipping',
	CANCEL_RETURN: 'cancel_return',
	RETURN: 'return',
	BUSINESS: 'business',
	AMOUNT: 'amount',
	AMOUNT_TYPE: 'amount_type',
	AMOUNT_OPTIONS: 'amount_options',
	LOGO_URL: 'cpp_header_image',
	DONATE_CONTEXT: 'donate_context',
	RECURRING_OPTION: 'recurring_option',
	PROGRAMNAMES: 'program_names'
}

// All supported client button props
export const CLIENT_BUTTON = {
	ID: 'id',
	CODE: 'code',
	TYPE: 'type',
	BUTTON_IMAGE: 'buttonImage',
	BUTTON_IMAGE_URL: 'buttonImageUrl',
	COUNTRY_CODE: 'countryCode',
	LANGUAGE: 'language',
	CURRENCY_CODE: 'currencyCode',
	LC: 'lc',
	BN: 'bn',
	ITEM_NAME: 'purpose',
	ITEM_NUMBER: 'itemNumber',
	NO_NOTE: 'noNote',
	NO_SHIPPING: 'noShipping',
	CANCEL_RETURN: 'cancelReturn',
	RETURN: 'return',
	BUSINESS: 'business',
	AMOUNT: 'amountValue',
	AMOUNT_TYPE: 'amountType',
	MERCHANT_AMOUNT_1: 'merchantAmount1',
	MERCHANT_AMOUNT_2: 'merchantAmount2',
	MERCHANT_AMOUNT_3: 'merchantAmount3',
	PURPOSE: 'purpose',
	USE_PAYPAL_BUTTON_STYLE: 'usePaypalButtonStyle',
	SHOW_ACCEPTED_CARD_LOGOS: 'showAcceptedCardLogos',
	BTN_SIZE: 'buttonSize',
	RECEIVER_CURRENCY: 'receiverCurrency',
	ACCOUNT_IDENTIFIER_TYPE: 'accountIdentifierType',
	ORGANIZATION_NAME: 'organizationName',
	LOGO_URL: 'logoUrl',
	USE_ACCOUNT_SETTINGS_LOGO: 'useSettingsLogo',
	SETTINGS_LOGO_URL: 'settingsLogoUrl',
	SHOW_ACCOUNT_LOGO_OPTIONS: 'showAccountLogoOptions',
	BUTTON_VARIABLES: 'extraVariablesView',
	RECURRING_OPTION: 'recurringEnabled',
	LISTPROGRAMS: 'listPrograms',
	PROGRAMNAMES: 'programNames'
}

/**
 * Gets value of a button property in the button_variables array
 * @param  {Object} button   Button object(server format)
 * @param  {String} property Property to get
 * @return {String}          Value of the property
 */
function getButtonVariable(button, property) {
	const buttonVar = _find(button.button_variables, (buttonVariable) => {
		return buttonVariable.name === property;
	})

	return _get(buttonVar, 'value');
}

/**
 * Adds button variable to the button_variables array
 * @param {Object} clientFormat Button object(client format)
 * @param {String} clientKey    Client property
 * @param {Object} serverFormat Button object(server format)
 * @param {String} serverKey    Server property
 */
function addButtonVariable(clientFormat, clientKey, serverFormat, serverKey) {
	if (clientFormat[clientKey] !== undefined) {
		serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
			name: serverKey,
			value: clientFormat[clientKey]
		})
	}
}

/**
 * Returns true if accepted card logos should be displayed
 */
export const shouldShowAcceptedCardLogos = (button) => {
	return !!(/CC_/.test(button.button_image_url));
}

const buttonSizeMap = {
	sm: 'small',
	md: 'medium',
	lg: 'large'
};

/**
 * Gets button size
 * @param  {Object} button Button
 * @return {String}        Size of the button ('small' or 'large')
 */
export const getButtonSize = (button) => {
	const size = /_(\D{2})\.gif/.exec(button.button_image_url); // eslint-disable-line
	if (size && size.length) {
		return buttonSizeMap[size[1].toLowerCase()];
	}

	// Default to 'small' ?
	return 'small';
}

/**
 * Gets button image and button image url
 */
export const getButtonImageAndUrl = (buttonSize, showAcceptedCardLogos, locale, country) => {
	const buttonImageUrl = getDonateButtonImageUrl(buttonSize, showAcceptedCardLogos, locale, country);
	let buttonImage = 'REG';
	if (showAcceptedCardLogos) {
		buttonImage = 'CC';
	}

	if (buttonSize === 'small') {
		buttonImage = 'SML';
	}

	return {
		button_image: buttonImage,
		button_image_url: buttonImageUrl
	}

}

/**
 * Returns true if its a Paypal button
 */
export const shouldUsePayPalButtonStyle = (button) => {
	const buttonImageUrl = button.button_image_url || '';
	if (buttonImageUrl.length) {
		if (
			buttonImageUrl.includes('msmaster') ||
			buttonImageUrl.includes('paypalobjects') ||
			buttonImageUrl.includes('paypal.com')
		) {
			return 'yes';
		}
	}

	return 'no';
}

export const getAccountIdentifierType = (value) => {
	// Is this an email address or encrypted_account_number
	if (value && value.indexOf('@') !== -1) {
		return ACCOUNT_IDENTIFIER_TYPES.EMAIL_ADDRESS;
	}
	return ACCOUNT_IDENTIFIER_TYPES.MERCHANT_ACCOUNT_ID;
}

export const getAccountIdentifierValue = (type) => {
	const reduxStore = getStore();
	const accountIdentifier = _get(reduxStore.getState(), 'application.accountIdentifier');
	if (type === ACCOUNT_IDENTIFIER_TYPES.EMAIL_ADDRESS) {
		return _get(accountIdentifier, 'email');
	}

	return _get(accountIdentifier, 'encrypted_account_number');
}

/**
 * This value is pre-populated in the logo url field.
 * P0 - Merchant experience url
 * P1 - Business image url
 * @return {String} Logo Url
 */
export const getClientValueForLogoUrl = (serverFormatButton) => {
	const cppHeaderImage = getButtonVariable(serverFormatButton, 'cpp_header_image');
	if (cppHeaderImage) {
		return cppHeaderImage;
	}

	const reduxStore = getStore();
	const merchantExperienceUrl = _get(reduxStore.getState(), 'application.merchantExperienceUrls.logoUrl');
	if (merchantExperienceUrl) {
		return merchantExperienceUrl;
	}

	const businessImageUrl = _get(reduxStore.getState(), 'application.businessImageUrls.url');
	if (businessImageUrl) {
		return businessImageUrl;
	}

	return '';
}

export const getLogoUrlFieldValues = (serverFormatButton) => {
	// Payment Settings logo url
	const reduxState = getStore().getState();
	const merchantExperienceUrl = _get(reduxState, 'application.merchantExperienceUrls.logoUrl');
	const businessImageUrl = _get(reduxState, 'application.businessImageUrls.url');

	// Set logo values for clientSide
	const settingsLogoUrl = merchantExperienceUrl ? merchantExperienceUrl : businessImageUrl;
	let logoUrl = getButtonVariable(serverFormatButton, 'cpp_header_image');
	const showAccountLogoOptions = merchantExperienceUrl || businessImageUrl;

	let useSettingsLogo = 'no';

	// If user has specified logo
	if (logoUrl) {
		// If url is same as "settings" url
		if (logoUrl === merchantExperienceUrl || logoUrl === businessImageUrl) {
			useSettingsLogo = 'yes';
			logoUrl = ''; // Don't display this value in the UI
		}
	} else {
		// User has not specified a logo url
		// If logo is present in "settings", use it
		if (merchantExperienceUrl || businessImageUrl) { // eslint-disable-line
			useSettingsLogo = 'yes';
		}
	}

	return {
		useSettingsLogo,
		logoUrl,
		settingsLogoUrl,
		showAccountLogoOptions
	}
}

/**
 * The logo value to be saved in the button
 * If user has entered a value, then save it in the button.
 * If not, no value should be saved
 * @param  {String} logoUrl User entered logo url
 * @return {String}         Logo Url to be saved in the button
 */
export const getServerValueForLogoUrl = (useSettingsLogo, logoUrl) => {
	if (useSettingsLogo === 'yes') {
		const reduxStore = getStore();
		const merchantExperienceUrl = _get(reduxStore.getState(), 'application.merchantExperienceUrls.logoUrl');

		if (merchantExperienceUrl) {
			// User has Logo in Payment Settings
			// Nothing to save in "button"
			return '';
		}

		const businessImageUrl = _get(reduxStore.getState(), 'application.businessImageUrls.url');
		if (businessImageUrl) {
			// User has Logo in Account Settings
			// This has to be saved in the "button"
			return businessImageUrl;
		}
	} else {
		// User wants to save the entered logoUrl
		return logoUrl;
	}
}

export const checkIfOldButton = (serverFormat) => {
	return _isEmpty(serverFormat[SERVER_BUTTON.DONATE_CONTEXT]);
}

// TODO: test if this function works once buttonfactoryserv is ready
export const getRecurringOption = (serverFormat) => {
	const isOldButton = checkIfOldButton(serverFormat);

	if (isOldButton) {
		const amountValue = getButtonVariable(serverFormat, SERVER_BUTTON.AMOUNT);
		// for button with fixed amount, default to false.
		return amountValue > 0 ? 'false' : 'true';
	}

	return serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.RECURRING_OPTION];
}

/**
 * Transform button from server format to client format
 */
export const transformServerFormatToClient = (serverFormat) => {
	const clientFormat = {};

	if (serverFormat.id) {
		clientFormat.id = serverFormat.id;
	}

	// Top level variables
	clientFormat[CLIENT_BUTTON.BUTTON_IMAGE] = serverFormat[SERVER_BUTTON.BUTTON_IMAGE];
	clientFormat[CLIENT_BUTTON.BUTTON_IMAGE_URL] = serverFormat[SERVER_BUTTON.BUTTON_IMAGE_URL];

	// variables from the button_variables array
	const noShipping = getButtonVariable(serverFormat, SERVER_BUTTON.NO_SHIPPING);
	clientFormat[CLIENT_BUTTON.NO_SHIPPING] = noShipping === '0' ? 'no' : 'yes';

	const noNote = getButtonVariable(serverFormat, SERVER_BUTTON.NO_NOTE);
	clientFormat[CLIENT_BUTTON.NO_NOTE] = noNote === '0' ? 'no' : 'yes';

	clientFormat[CLIENT_BUTTON.RECEIVER_CURRENCY] = getButtonVariable(serverFormat, SERVER_BUTTON.CURRENCY_CODE);
	clientFormat[CLIENT_BUTTON.CANCEL_RETURN] = getButtonVariable(serverFormat, SERVER_BUTTON.CANCEL_RETURN);
	clientFormat[CLIENT_BUTTON.RETURN] = getButtonVariable(serverFormat, SERVER_BUTTON.RETURN);
	clientFormat[CLIENT_BUTTON.PURPOSE] = getButtonVariable(serverFormat, SERVER_BUTTON.ITEM_NAME);
	clientFormat[CLIENT_BUTTON.ITEM_NUMBER] = getButtonVariable(serverFormat, SERVER_BUTTON.ITEM_NUMBER);
	clientFormat[CLIENT_BUTTON.ACCOUNT_IDENTIFIER_TYPE] = getAccountIdentifierType(getButtonVariable(serverFormat, SERVER_BUTTON.BUSINESS));
	clientFormat[CLIENT_BUTTON.BUSINESS] = getButtonVariable(serverFormat, SERVER_BUTTON.BUSINESS);

	const countryCode = serverFormat[SERVER_BUTTON.COUNTRY_CODE];
	clientFormat[CLIENT_BUTTON.COUNTRY_CODE] = countryCode;
	clientFormat[CLIENT_BUTTON.LANGUAGE] = serverFormat[SERVER_BUTTON.LANGUAGE];

	// Derived variables
	clientFormat[CLIENT_BUTTON.USE_PAYPAL_BUTTON_STYLE] = shouldUsePayPalButtonStyle(serverFormat);
	clientFormat[CLIENT_BUTTON.SHOW_ACCEPTED_CARD_LOGOS] = shouldShowAcceptedCardLogos(serverFormat);
	clientFormat[CLIENT_BUTTON.BTN_SIZE] = getButtonSize(serverFormat);

	const logoFieldValues = getLogoUrlFieldValues(serverFormat);
	clientFormat[CLIENT_BUTTON.LOGO_URL] = logoFieldValues.logoUrl;
	clientFormat[CLIENT_BUTTON.USE_ACCOUNT_SETTINGS_LOGO] = logoFieldValues.useSettingsLogo;
	clientFormat[CLIENT_BUTTON.SETTINGS_LOGO_URL] = logoFieldValues.settingsLogoUrl;
	clientFormat[CLIENT_BUTTON.SHOW_ACCOUNT_LOGO_OPTIONS] = logoFieldValues.showAccountLogoOptions;

	const supportedVariables = getSupportedVariables(serverFormat.button_variables);
	clientFormat.extraVariablesView = formatVariablesForTextArea(supportedVariables);

	const unsupportedVariables = getUnsupportedVariables(serverFormat.button_variables);
	clientFormat.unsupportedVariables = unsupportedVariables;

	const amount = getButtonVariable(serverFormat, SERVER_BUTTON.AMOUNT);
	const currency = clientFormat[CLIENT_BUTTON.RECEIVER_CURRENCY];

	if (amount !== undefined) {
		clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] = 'fixed';
		clientFormat[CLIENT_BUTTON.AMOUNT] = formatAmountFromServer(amount, currency);
	} else {
		clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] = 'variable';
	}

	const recurringOption = getRecurringOption(serverFormat);
	clientFormat[CLIENT_BUTTON.RECURRING_OPTION] = recurringOption === 'true' ? 'yes' : 'no';

	if (!checkIfOldButton(serverFormat)) {
		// For 'fixed' amounttype, do not read from 'donate_context'
		if (clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] !== 'fixed') {
			let amountType = serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.AMOUNT_TYPE]
			if (!amountType) {
				amountType = 'variable';
			}
			amountType = amountType.toLowerCase();
			clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] = amountType;

			if (clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] === 'merchant_recommended') {
				clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_1] = formatAmountFromServer(_get(serverFormat[SERVER_BUTTON.DONATE_CONTEXT], `${SERVER_BUTTON.AMOUNT_OPTIONS}[0]`), currency);
				clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_2] = formatAmountFromServer(_get(serverFormat[SERVER_BUTTON.DONATE_CONTEXT], `${SERVER_BUTTON.AMOUNT_OPTIONS}[1]`), currency);
				clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_3] = formatAmountFromServer(_get(serverFormat[SERVER_BUTTON.DONATE_CONTEXT], `${SERVER_BUTTON.AMOUNT_OPTIONS}[2]`), currency);
			}
		}

		const programNames = serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.PROGRAMNAMES];
		if (!_isEmpty(programNames)) {
			clientFormat[CLIENT_BUTTON.LISTPROGRAMS] = 'yes';
			clientFormat[CLIENT_BUTTON.PROGRAMNAMES] = programNames;
		} else {
			clientFormat[CLIENT_BUTTON.LISTPROGRAMS] = 'no';
			clientFormat[CLIENT_BUTTON.PROGRAMNAMES] = [];
		}
	}

	// case: 'NEW PROGRAMS'
	// If it is V1 button, convert V1Program to V2Program
	// If it is V2 button, nothing to convert
	if (checkIfOldButton(serverFormat)) {
		const v0Program = getButtonVariable(serverFormat, SERVER_BUTTON.ITEM_NUMBER);
		if (v0Program) {
			clientFormat[CLIENT_BUTTON.LISTPROGRAMS] = 'yes';
			clientFormat[CLIENT_BUTTON.PROGRAMNAMES] = [v0Program];
		}
	}

	return clientFormat;
}

/**
 * Transform button from client format to server format
 */
export const transformClientFormatToServer = (clientFormat) => {
	let serverFormat = {};

	if (clientFormat.id) {
		serverFormat[SERVER_BUTTON.ID] = clientFormat.id;
	}

	const reduxStore = getStore();
	const reduxState = reduxStore.getState();
	const isLoggedIn = isLoggedInUser(_get(reduxState, 'application'));
	const isHosted = _includes(['BUSINESS', 'PREMIER'], _get(reduxState, 'application.userType'));
	const isMerchantIdEnabled = _includes(['BUSINESS', 'PREMIER', 'PERSONAL'], _get(reduxState, 'application.userType'));
	const isProgramEnabled = _get(reduxStore.getState(), 'application.enableProgram');
	const isMerchantPresetAmountEnabled = _get(reduxStore.getState(), 'application.enableMerchantPresetAmount');
	const isRecurringEnabled = _get(reduxStore.getState(), 'application.enableRecurring');

	serverFormat[SERVER_BUTTON.COUNTRY_CODE] = clientFormat.countryCode;
	serverFormat[SERVER_BUTTON.TYPE] = 'DONATE';
	serverFormat[SERVER_BUTTON.SUBTYPE] = 'PRODUCTS';
	serverFormat[SERVER_BUTTON.LANGUAGE] = clientFormat.language;

	// Create array of `button_variables`
	serverFormat[SERVER_BUTTON.BUTTON_VARIABLES] = [];
	addButtonVariable(clientFormat, CLIENT_BUTTON.PURPOSE, serverFormat, SERVER_BUTTON.ITEM_NAME);
	addButtonVariable(clientFormat, CLIENT_BUTTON.CANCEL_RETURN, serverFormat, SERVER_BUTTON.CANCEL_RETURN);
	addButtonVariable(clientFormat, CLIENT_BUTTON.RETURN, serverFormat, SERVER_BUTTON.RETURN);
	addButtonVariable(clientFormat, CLIENT_BUTTON.RECEIVER_CURRENCY, serverFormat, SERVER_BUTTON.CURRENCY_CODE);

	// logo_url button_variable
	// This field should only be included if user has entered logo url
	const logoUrl = getServerValueForLogoUrl(clientFormat.useSettingsLogo, clientFormat.logoUrl);
	if (logoUrl) {
		serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
			name: SERVER_BUTTON.LOGO_URL,
			value: logoUrl
		});
	}

	// Business field
	if (isLoggedIn && isMerchantIdEnabled) {
		const accountIdentifierValue = getAccountIdentifierValue(clientFormat.accountIdentifierType);
		if (accountIdentifierValue) {
			serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
				name: SERVER_BUTTON.BUSINESS,
				value: accountIdentifierValue
			});
		}
	} else {
		clientFormat[CLIENT_BUTTON.BUSINESS] = (clientFormat[CLIENT_BUTTON.BUSINESS] || '').toLowerCase();
		addButtonVariable(clientFormat, CLIENT_BUTTON.BUSINESS, serverFormat, SERVER_BUTTON.BUSINESS);
	}

	// Amount field
	const amountType = clientFormat[CLIENT_BUTTON.AMOUNT_TYPE];
	if (amountType === 'fixed' && clientFormat[CLIENT_BUTTON.AMOUNT]) {
		serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
			name: SERVER_BUTTON.AMOUNT,
			value: normalizeNumber(clientFormat[CLIENT_BUTTON.AMOUNT])
		})
	}


	if (isHosted) {
		// Generate donate_context
		// Set `amount_type`
		if (isMerchantPresetAmountEnabled || isRecurringEnabled || isProgramEnabled) {
			serverFormat[SERVER_BUTTON.DONATE_CONTEXT] = {};
		}

		// Create array of `amount_options`
		if (isMerchantPresetAmountEnabled && amountType !== 'fixed') {
			serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.AMOUNT_TYPE] = amountType.toUpperCase();

			if (amountType === 'merchant_recommended') {
				serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.AMOUNT_OPTIONS] = [];
				serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.AMOUNT_OPTIONS].push(normalizeNumber(clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_1]));
				serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.AMOUNT_OPTIONS].push(normalizeNumber(clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_2]));
				serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.AMOUNT_OPTIONS].push(normalizeNumber(clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_3]));
			}
		}

		// Recurring option
		if (isRecurringEnabled) {
			serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.RECURRING_OPTION] = clientFormat[CLIENT_BUTTON.RECURRING_OPTION] === 'yes' ? 'true' : 'false';
		}

		// Program Names
		if (isProgramEnabled) {
			if (clientFormat[CLIENT_BUTTON.LISTPROGRAMS] === 'yes') {
				serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.PROGRAMNAMES] = clientFormat[CLIENT_BUTTON.PROGRAMNAMES];
			} else {
				serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.PROGRAMNAMES] = [];
			}
		}
	}

	// Handle ITEM_NUMBER
	// When editing a V1 button in V2, ITEM_NUMBER has to be set to ""
	if (isHosted) {
		if (isProgramEnabled) {
			serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
				name: SERVER_BUTTON.ITEM_NUMBER,
				value: ''
			})
		} else {
			addButtonVariable(clientFormat, CLIENT_BUTTON.ITEM_NUMBER, serverFormat, SERVER_BUTTON.ITEM_NUMBER);
		}
	} else {
		addButtonVariable(clientFormat, CLIENT_BUTTON.ITEM_NUMBER, serverFormat, SERVER_BUTTON.ITEM_NUMBER);
	}

	// no_shipping field
	const noShipping = clientFormat[CLIENT_BUTTON.NO_SHIPPING];
	serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
		name: SERVER_BUTTON.NO_SHIPPING,
		value: noShipping === 'no' ? 0 : 1
	});

	// no_note field
	const noNote = clientFormat[CLIENT_BUTTON.NO_NOTE];
	serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
		name: SERVER_BUTTON.NO_NOTE,
		value: noNote === 'no' ? 0 : 1
	});

	// Add button_image and button_image_url
	if (clientFormat.usePaypalButtonStyle === 'no') {
		serverFormat.button_image_url = clientFormat.buttonImageUrl;
	} else {
		const buttonImageProps = getButtonImageAndUrl(
			clientFormat.buttonSize,
			clientFormat.showAcceptedCardLogos,
			clientFormat.language,
			clientFormat.countryCode
		);
		serverFormat.button_image_url = buttonImageProps.button_image_url;
		serverFormat.button_image = buttonImageProps.button_image;
	}

	if (clientFormat.extraVariablesView) {
		const extraVariables = parseTextAreaValue(clientFormat.extraVariablesView);
		_each(extraVariables, (variable) => {
			serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push(variable)
		})
	}

	return serverFormat;
}

export function mapServerFieldToClientField(server_field) {
	const field = _findKey(SERVER_BUTTON, (item) => (item === server_field));
	return CLIENT_BUTTON[field];
}



// WEBPACK FOOTER //
// ./buttons/lib/buttonModel.js